import { defineStore } from "pinia";
import { ref, watch } from "vue";
import bus from "@/bus";

import { fetchBunchOfProfiles } from "@/api/repositories/discovery.js";
import {
  guestProfile,
  updateProfile as updateProfileRequest,
} from "@/api/repositories/user";
import {
  commitLike,
  commitDislike,
  commitReport,
} from "@/api/repositories/actions.js";

import { logEvent } from "@/utils/tracking.js";
import trackedEvents from "@/utils/trackedEvents";
import { IS_CLOUDINARY_URL } from "@/utils/helpers";

import {
  PROFILES_TTL,
  FLOW_CARD_PUSH_TTL,
  ACTION_ALREADY_EXIST_STATUS_CODE,
  MIN_PROFILE_CARDS_BEFORE_INJECTION,
  MAX_PROFILE_CARDS_BEFORE_INJECTION,
  APP_VERSION,
} from "@/constants";
import { shouldInvalidateCache } from "@/utils/version.js";

import { useProfileStore } from "./profile";
import { useSurveysStore } from "./surveys";
import { usePulseStore } from "./pulse";

export const useProfilesStore = defineStore(
  "profiles",
  () => {
    const profileStore = useProfileStore();
    const surveysStore = useSurveysStore();
    const pulseStore = usePulseStore();

    const profiles = ref([]);
    const discoveryFeed = ref([]);
    const rules = ref({
      age: { min: 18, max: 70 },
      distance: 100,
      match_algo_version: 3,
      onlyCommunityMode: false,
      lookingForEnumValueId: null,
      adjustDistanceIfNoProfiles: true,
      adjustAgeIfNoProfiles: true,
      adjustLookingForIfNoProfiles: true,
    });
    const fetchingAdditionalProfiles = ref(false);
    const fetchedProfilesTimestamp = ref(0);
    const cacheAppVersion = ref(null);
    const boostedProfile = ref(undefined);
    const profileCardsSinceLastInjection = ref(0);
    const surveyCards = ref([]);
    const updateCards = ref([]);
    const oldProfileIds = new Set();
    const profilesLoading = ref(false);
    const triggerUpdateCardPushTimestamp = ref(0);
    const updateCardPushTriggerUser = ref(null);
    const matches = ref([]);
    const matchedProfile = ref(undefined);
    const discoveryFeedExhausted = ref(false);

    let nextInjectionThreshold = MIN_PROFILE_CARDS_BEFORE_INJECTION;

    const setProfiles = (newProfiles) => {
      profiles.value = newProfiles;
    };

    const appendProfiles = (newProfiles) => {
      profiles.value = [...profiles.value, ...newProfiles];
    };

    const resetProfiles = () => {
      profiles.value = [];
      discoveryFeed.value = [];
    };

    const setSurveyCards = (newSurveyCards) => {
      surveyCards.value = newSurveyCards;
    };

    const setUpdateCards = (newUpdateCards) => {
      updateCards.value = newUpdateCards;
    };

    const setBoostedProfile = (profile) => {
      boostedProfile.value = profile;
    };

    const removeBoostedProfile = () => {
      boostedProfile.value = undefined;
    };

    /**
     * Determines and returns the next item to add to the feed, removing it from its source.
     * Returns null if no items are available.
     */
    const getNextFeedItem = (profilesOnly = false) => {
      let nextItem = null;
      let itemType = null; // 'profile', 'survey', 'update'

      // Injection Decision
      const canInject =
        !profilesOnly &&
        (surveyCards.value.length > 0 || updateCards.value.length > 0);
      const shouldInject =
        canInject &&
        profileCardsSinceLastInjection.value >= nextInjectionThreshold;

      if (shouldInject) {
        const availableInjectables = [];
        if (surveyCards.value.length > 0) availableInjectables.push("survey");
        if (updateCards.value.length > 0) availableInjectables.push("update");

        const chosenType =
          availableInjectables[
            Math.floor(Math.random() * availableInjectables.length)
          ];

        if (chosenType === "survey") {
          nextItem = surveyCards.value.shift();
          itemType = "survey";
        } else {
          nextItem = updateCards.value.shift();
          itemType = "update";
        }

        profileCardsSinceLastInjection.value = 0;
        nextInjectionThreshold = getRandomInjectionThreshold();
      }

      // Fallback to using a profile if no available item to inject
      if (!nextItem && profiles.value.length > 0) {
        nextItem = profiles.value.shift();
        itemType = "profile";
        profileCardsSinceLastInjection.value++;
      }

      return nextItem ? { item: nextItem, type: itemType } : null;
    };

    const getRandomInjectionThreshold = () => {
      return (
        Math.floor(
          Math.random() *
            (MAX_PROFILE_CARDS_BEFORE_INJECTION -
              MIN_PROFILE_CARDS_BEFORE_INJECTION +
              1)
        ) + MIN_PROFILE_CARDS_BEFORE_INJECTION
      );
    };

    const fillDiscoveryFeed = (profilesOnly = false) => {
      while (discoveryFeed.value.length < 2) {
        const newItem = getNextFeedItem(profilesOnly);
        if (newItem) {
          discoveryFeed.value.push(newItem);
        } else {
          break;
        }
      }

      discoveryFeedExhausted.value = discoveryFeed.value.length === 0;
    };

    const updateProfile = (profileId, profileData) => {
      profiles.value = profiles.value.map((item) => {
        if (item.id === profileId) {
          return {
            ...item,
            ...profileData,
            distance: item.distance,
          };
        }
        return item;
      });

      return profiles.value.find((item) => item.id === profileId);
    };

    const updateProfileByUserId = (userId, profileData) => {
      const profile = profiles.value.find((item) => item.user_id === userId);
      if (profile) {
        return updateProfile(profile.id, profileData);
      }
    };

    const getUserById = async (userId, fromLocal = true) => {
      if (profiles.value.length > 0 && fromLocal) {
        const item = profiles.value.find(
          (item) => item.id.toString() === userId.toString()
        );
        if (item) {
          return item;
        }
      }

      return await guestProfile(userId);
    };

    const addToOldProfileIds = (profileIds) => {
      profileIds.forEach((id) => {
        oldProfileIds.add(id);
      });
    };

    const makeProfilesApiCall = async (
      { isAdditional } = { isAdditional: false }
    ) => {
      if (!isAdditional) {
        oldProfileIds.clear();
      }
      const result = await fetchBunchOfProfiles(
        isAdditional ? Array.from(oldProfileIds) : []
      );
      return result.users;
    };

    /**
     * Fetches the initial set of profiles based on the provided location information.
     * If profiles are already loaded and are still valid, it uses the cached profiles
     * unless forceRefresh is set to true. Updates the profiles loading state and
     * sets the timestamp for when the profiles were fetched.
     *
     * @param {{ latitude: number?, longitude: number? }} locationInfo - The location information for fetching profiles.
     * @param {boolean} [forceRefresh=false] - If true, forces a refresh of profiles
     * even if the cached profiles are still valid.
     */
    const fetchInitialProfiles = async (
      locationInfo = {},
      forceRefresh = false
    ) => {
      if (isCacheValid() && !forceRefresh) {
        return;
      }

      discoveryFeedExhausted.value = false;
      profilesLoading.value = true;
      try {
        if (locationInfo.latitude && locationInfo.longitude) {
          await updateProfileRequest(locationInfo);
        }
        const [newProfiles, pendingUpdateCards] = await Promise.all([
          makeProfilesApiCall(),
          profileStore.getPendingUpdates(),
        ]);
        setProfiles(newProfiles);
        setUpdateCards(pendingUpdateCards);

        surveysStore
          .getSurveys()
          .then((newSurveyCards) => {
            setSurveyCards(newSurveyCards);
          })
          .catch(() => {});
        addToOldProfileIds(newProfiles.map((p) => p.id));
      } catch (ex) {
        console.error(ex);
      } finally {
        fetchedProfilesTimestamp.value = Date.now();
        cacheAppVersion.value = APP_VERSION;
        profilesLoading.value = false;
        fillDiscoveryFeed(true);
      }
    };

    const isCacheValid = () => {
      if (!fetchedProfilesTimestamp.value) {
        return false;
      }

      // Invalidate cache if it was created with an older version
      if (shouldInvalidateCache(APP_VERSION, cacheAppVersion.value)) {
        return false;
      }

      return (
        profiles.value.length > 0 &&
        Date.now() - fetchedProfilesTimestamp.value < PROFILES_TTL
      );
    };

    const fetchAdditionalProfiles = async () => {
      if (fetchingAdditionalProfiles.value) {
        return;
      }
      discoveryFeedExhausted.value = false;
      fetchingAdditionalProfiles.value = true;

      if (oldProfileIds.size === 0 && profiles.value.length > 0) {
        addToOldProfileIds(profiles.value.map((p) => p.id));
      }

      try {
        const [newProfiles, pendingUpdateCards] = await Promise.all([
          makeProfilesApiCall({ isAdditional: true }),
          profileStore.getPendingUpdates(),
        ]);
        appendProfiles(newProfiles);
        setUpdateCards(pendingUpdateCards);

        surveysStore
          .getSurveys()
          .then((newSurveyCards) => {
            setSurveyCards(newSurveyCards);
          })
          .catch(() => {});
        addToOldProfileIds(newProfiles.map((p) => p.id));
      } catch (error) {
        console.error(error);
      } finally {
        fetchingAdditionalProfiles.value = false;
        fillDiscoveryFeed();
      }
    };

    const removeProfile = (profileId) => {
      discoveryFeed.value.shift();
      setProfiles(profiles.value.filter((item) => item.id !== profileId));
      fillDiscoveryFeed();
      if (profiles.value.length <= 5) {
        fetchAdditionalProfiles();
      }
    };

    const removeProfileById = (profileId) => {
      // Remove from discoveryFeed by searching for the profile
      discoveryFeed.value = discoveryFeed.value.filter(
        (feedItem) => feedItem.item.id !== profileId
      );

      // Remove from profiles array
      setProfiles(profiles.value.filter((item) => item.id !== profileId));

      fillDiscoveryFeed();
      if (profiles.value.length <= 5) {
        fetchAdditionalProfiles();
      }
    };

    const updateLastProfile = async () => {
      if (profiles.value.length === 0) {
        return;
      }
      updateProfile(
        profiles.value[0].id,
        (await guestProfile(profiles.value[0].id)).profile
      );
    };

    const doesUserHaveBio = (user) =>
      (user.properties ?? []).filter((p) => p.category_name === "Bio").length >
      0;

    const doesUserHavePrompts = (user) =>
      (user.properties ?? []).filter((p) => p.category_name === "Sprinkle")
        .length > 0;

    const doesUserHaveInterests = (user) =>
      (user.properties ?? []).filter((p) => p.category_name === "Interests")
        .length > 0;

    const likeProfile = async (
      actionable_id,
      actionable_type,
      comment,
      user,
      fromBoosted = false,
      shouldEmitMatchNotification = true
    ) => {
      try {
        const response = await commitLike({
          fromBoosted,
          actionable_id,
          actionable_type,
          comment,
        });
        const eventProperties = {
          verified: user.person_verification,
          number_of_images: user.gallery?.length
            ? 1 + user.gallery.length
            : null,
          description_length: user.description?.length
            ? user.description?.length
            : null,
          matched: response.is_match,
          profile_actionable: user.actionable || false,
          is_boosted_profile: user.is_boosted,
          is_cloudinary: IS_CLOUDINARY_URL.test(user.avatar),
          user_id: user.id,
          actionable_type,
          has_comment: comment.length > 0,
          has_prompts: doesUserHavePrompts(user),
          has_bio: doesUserHaveBio(user),
          has_interests: doesUserHaveInterests(user),
        };

        // Include comment text for analytics when available
        if (comment.length > 0) {
          eventProperties.comment = comment;
        }

        logEvent(trackedEvents.PROFILE_LIKED, eventProperties);

        if (response.is_match && shouldEmitMatchNotification) {
          matches.value = [...matches.value, user];
        } else {
          if (
            window.SababuuNativeBridge &&
            profileStore.canAskForNativePush &&
            !profileStore.isNativePushSubscribed &&
            Date.now() - triggerUpdateCardPushTimestamp.value >
              FLOW_CARD_PUSH_TTL
          ) {
            discoveryFeed.value.splice(1, 0, {
              type: "update",
              item: { id: "push-permission" },
            });
            updateCardPushTriggerUser.value = user;
            triggerUpdateCardPushTimestamp.value = Date.now();
          }
        }
        return response;
      } catch (ex) {
        if (ex?.data?.response?.status === ACTION_ALREADY_EXIST_STATUS_CODE) {
          return;
        }
        console.error(ex);
        bus.global.emit("notification.top:show", {
          type: "error",
          message: "Something went wrong when liking profiles.",
        });
      }
    };

    const dislikeProfile = async (profile, fromBoosted = false) => {
      try {
        await commitDislike({
          userId: profile.id,
          fromBoosted,
        });
        logEvent(trackedEvents.PROFILE_DISLIKED, {
          user_id: profile.id,
          verified: profile.person_verification,
          number_of_images: profile.gallery?.length
            ? 1 + profile.gallery.length
            : null,
          description_length: profile.description?.length
            ? profile.description?.length
            : null,
          profile_actionable: profile.actionable || false,
          is_boosted_profile: profile.is_boosted,
          is_cloudinary: IS_CLOUDINARY_URL.test(profile.avatar),
          has_prompts: doesUserHavePrompts(profile),
          has_bio: doesUserHaveBio(profile),
          has_interests: doesUserHaveInterests(profile),
        });
      } catch (ex) {
        if (ex?.data?.response?.status === ACTION_ALREADY_EXIST_STATUS_CODE) {
          return;
        }
        console.error(ex);
        bus.global.emit("notification.top:show", {
          type: "error",
          message: "Something went wrong when passing profile.",
        });
      }
    };

    const reportProfile = async (
      profile,
      fromBoosted = false,
      reportReason = ""
    ) => {
      try {
        await commitReport({
          fromBoosted,
          userId: profile.id,
        });
        logEvent(trackedEvents.PROFILE_REPORTED, {
          user_id: profile.id,
          verified: profile.person_verification,
          number_of_images: profile.gallery?.length
            ? 1 + profile.gallery.length
            : null,
          description_length: profile.description?.length
            ? profile.description?.length
            : null,
          profile_actionable: profile.actionable || false,
          is_boosted_profile: profile.is_boosted,
          report_comment: reportReason || null,
          is_cloudinary: IS_CLOUDINARY_URL.test(profile.avatar),
          has_prompts: doesUserHavePrompts(profile),
          has_bio: doesUserHaveBio(profile),
          has_interests: doesUserHaveInterests(profile),
        });

        // Remove profile from discovery feed (search and remove, not shift)
        removeProfileById(profile.id);

        // Remove user from pulse feed if they exist
        pulseStore.removePulseEntriesFromUser(profile.id);
      } catch (ex) {
        if (ex?.data?.response?.status === ACTION_ALREADY_EXIST_STATUS_CODE) {
          return;
        }
        console.error(ex);
        bus.global.emit("notification.top:show", {
          type: "error",
          message: "Something went wrong when reporting profile.",
        });
      }
    };

    function removeSurveyCard(id) {
      discoveryFeed.value.shift();
      setSurveyCards(surveyCards.value.filter((card) => card.id !== id));
      fillDiscoveryFeed();
    }

    const surveyCardAnswered = async (survey) => {
      removeSurveyCard(survey.id);
    };

    const surveyCardDismissed = async (survey) => {
      removeSurveyCard(survey.id);
    };

    function removeUpdateCard(id) {
      profileStore.updateCardSeen(id);
      setUpdateCards(updateCards.value.filter((card) => card.id !== id));
      discoveryFeed.value.shift();
      fillDiscoveryFeed();
    }

    // Watch for intentional profile settings updates and reset profiles
    watch(
      () => profileStore.settingsUpdatedAt,
      (newTimestamp) => {
        if (newTimestamp) {
          resetProfiles();
        }
      }
    );

    return {
      profiles,
      profilesLoading,
      rules,
      discoveryFeed,
      fetchedProfilesTimestamp,
      cacheAppVersion,
      boostedProfile,
      matches,
      matchedProfile,
      discoveryFeedExhausted,
      setProfiles,
      likeProfile,
      dislikeProfile,
      reportProfile,
      updateProfile,
      getUserById,
      fetchInitialProfiles,
      fetchAdditionalProfiles,
      removeProfile,
      removeProfileById,
      updateLastProfile,
      updateProfileByUserId,
      setBoostedProfile,
      removeBoostedProfile,
      surveyCardAnswered,
      surveyCardDismissed,
      profileCardsSinceLastInjection,
      surveyCards,
      updateCards,
      removeUpdateCard,
      resetProfiles,
      triggerUpdateCardPushTimestamp,
      updateCardPushTriggerUser,
    };
  },
  {
    persist: {
      paths: [
        "discoveryFeed",
        "discoveryFeedExhausted",
        "fetchedProfilesTimestamp",
        "cacheAppVersion",
        "hasMorePulseEntries",
        "matches",
        "profileCardsSinceLastInjection",
        "profiles",
        "rules",
        "surveyCards",
        "triggerUpdateCardPushTimestamp",
        "updateCardPushTriggerUser",
        "updateCards",
      ],
    },
  }
);
